import { Injectable } from '@angular/core';
import { PermissionCheckerService, ServiceRouter } from '@pershing/pershing-core';
import { QuoteToggleServiceDefinition } from './quotetoggle-service-definition';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { QuoteRef } from '../models/quoteRef-model';
import { QuoteToggleConstants } from '../utils/quotetoggle-constants';

@Injectable({
    providedIn: 'root'
})
export class QuoteToggleService {
    private selectedQuoteType: string = QuoteToggleConstants.DELAYED_QUOTE;
    quotetoggleServiceUrl: string = '';
    public sessionRealtimeQuote$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false); // not being used
    public sessionAgreementSigned$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false); // no one listen to this
    public quoteReference$: BehaviorSubject<QuoteRef[]> = new BehaviorSubject<QuoteRef[]>(null);
    public exchangeAgreeResponse$: BehaviorSubject<any> = new BehaviorSubject<false>(null); // emit when exchange agreement signed
    quoteReferenceList: QuoteRef[] = [];
    private _selectQuoteType: Subject<string> = new Subject<string>();
    selectQuoteType$: Observable<string> = this._selectQuoteType.asObservable();
    private _updateAsOfDate: Subject<any> = new Subject<any>();
    updateAsOfDate$: Observable<any> = this._updateAsOfDate.asObservable();
    private _hasSignedExchangeAgreement: boolean = false;
    private _rglDateRefresh: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    rglDateRefresh$: Observable<boolean> = this._rglDateRefresh.asObservable();
    public refreshButtonPressed$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public static BALANCES = 'BALANCES';
    public static HOLDINGS = 'HOLDINGS';
    public static UGL = 'UGL';
    public static QUICKQUOTE = 'QUICKQUOTE';

    constructor(private serviceRouter: ServiceRouter, private authorizationService: PermissionCheckerService) {
        this.quotetoggleServiceUrl = this.serviceRouter.getServiceRouteURL(QuoteToggleServiceDefinition.getInstance());

        if (this.sessionStorageHas(QuoteToggleConstants.QUOTE_TYPE)) {
            this.selectedQuoteType = sessionStorage.getItem(QuoteToggleConstants.QUOTE_TYPE);
        } else {
            sessionStorage.setItem(QuoteToggleConstants.QUOTE_TYPE, QuoteToggleConstants.DELAYED_QUOTE);
        }
    }

    updateQuoteRef(data: QuoteRef) {
        // quote reference is per page, check if page never visisted, then add else, update page quote ref.
        if (this.quoteReferenceList.filter(item => { return item.pageName === data.pageName; }).length === 0) {
            this.quoteReferenceList.push(data);
        } else {
            this.quoteReferenceList.forEach(item => {
                if (item.pageName === data.pageName) {
                    item.asOfDate = data.asOfDate;
                    item.agreementSigned = data.agreementSigned;
                    item.realTimeRefOn = data.realTimeRefOn;
                    item.quoteAction = data.quoteAction;
                    item.isRTQuoteEntitled = data.isRTQuoteEntitled;
                }
                // synch up all other pages reference.  It's up to the page to further add logic e.g. agreement.
                if (data.quoteAction === QuoteToggleConstants.TOGGLE_ACTION) {
                    item.realTimeRefOn = data.realTimeRefOn;
                    this.sessionRealtimeQuote$.next(data.realTimeRefOn);
                } else if (data.quoteAction === QuoteToggleConstants.TOGGLE_ON_AGREE) {
                    item.realTimeRefOn = data.realTimeRefOn;
                    this.sessionRealtimeQuote$.next(data.realTimeRefOn);
                    this.sessionAgreementSigned$.next(data.agreementSigned);
                }
            });
        }
        this.quoteReference$.next(this.quoteReferenceList);
    }

    selectQuoteType(quoteType: string): void {
        sessionStorage.setItem(QuoteToggleConstants.QUOTE_TYPE, quoteType);
        this._selectQuoteType.next(quoteType);
    }

    getSelectedQuoteType(pageId: string): string {
        if (this.sessionStorageHas(QuoteToggleConstants.QUOTE_TYPE)) {
            let quoteType = sessionStorage.getItem(QuoteToggleConstants.QUOTE_TYPE)

            if(quoteType === QuoteToggleConstants.REALTIME_QUOTE && !this.isEntitlementsForThePage(pageId)) {
                this.selectQuoteType(QuoteToggleConstants.DELAYED_QUOTE);
                this.selectedQuoteType = QuoteToggleConstants.DELAYED_QUOTE;
            } else {
                this.selectedQuoteType = sessionStorage.getItem(QuoteToggleConstants.QUOTE_TYPE);
            }
        }

        return this.selectedQuoteType;
    }

    isEntitlementsForThePage(pageId: string): boolean {
        if (pageId === QuoteToggleService.BALANCES) {
            return this.authorizationService.isEntitled('REAL_TIME_EQUITY_QUOTES'); // intentional equity entitlement, based on Anna and Karen's approval
        } else if (pageId === QuoteToggleService.UGL) {
            return this.authorizationService.isEntitled("UGL_WITH_RT_QUOTES");
        } else if (pageId === QuoteToggleService.HOLDINGS) {
            return this.authorizationService.isEntitled("HOLDINGS_WITH_RT_QUOTES");
        } else if (pageId === QuoteToggleService.QUICKQUOTE) {
            return this.authorizationService.isEntitled("REAL_TIME_EQUITY_QUOTES");
        }
        return false;
    }

    updateAsOfDate(asOfDate: any): void {
        this._updateAsOfDate.next(asOfDate);
    }
    rglDateRefresh(isRgl: boolean): void {
        this._rglDateRefresh.next(isRgl);
    }
    hasSignedExchangeAgreement() {
        if (this.sessionStorageHas(QuoteToggleConstants.SIGNED_EXCHANGE_AGREEMENT)) {
            this._hasSignedExchangeAgreement = sessionStorage.getItem(QuoteToggleConstants.SIGNED_EXCHANGE_AGREEMENT) === 'true';
        }
        return this._hasSignedExchangeAgreement;
    }
    signedExchangeAgreement(signed: boolean) {
        sessionStorage.setItem(QuoteToggleConstants.SIGNED_EXCHANGE_AGREEMENT, signed.toString());
        this._hasSignedExchangeAgreement = signed;
    }
    sessionStorageHas(key: string): boolean {
        return !!sessionStorage.getItem(key);
    }

    refreshButtonPressed() {
        this.refreshButtonPressed$.next(true);
    }
}
